import React from 'react';
import {
  Box,
  BoxWithShadow,
  ButtonWithLink,
  constants,
  Spinner,
  SolidDivider,
} from '@thecb/components';
import { useDispatch, useSelector } from 'react-redux';
import {
  ORDER_STATUS,
  PAYMENT_TYPE,
  TRANSACTION_STATUS,
} from '../../utils/constants';
import { initiateVoidTransactionById } from '../../pages/cart/Cart.state';
import { useLocation } from 'react-router-dom';
import PaymentDetails from '../payment-details';
import { isDatacapTransaction } from '../../utils/helpers';

const CartSidebar = ({ orderItems, isLoading }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const centsMultiplier = 100;
  const lineItems = orderItems.map((item) => ({
    amount: item.recorded_subtotal * centsMultiplier,
    description: item.receivable_category,
    quantity: item.quantity,
    subDescription: item.external_attributes?.description,
    is_negative_amount: item.is_negative_amount,
  }));
  const {
    transaction_details = [],
    remaining_balance,
    total,
  } = useSelector((state) => state.cart).order;
  const { creditCardReaderType } = useSelector(
    (state) => state.shared.clientConfig,
  );
  const { order } = useSelector((state) => state.cart);
  const createVoidText = (transaction) => {
    switch (transaction.payment_type) {
      case PAYMENT_TYPE.CASH_PAYMENT:
        return 'Cash';
      case PAYMENT_TYPE.CARD_PAYMENT:
        return `Card ending in ${transaction.credit_card.last_four}`;
      case PAYMENT_TYPE.CHECK_PAYMENT:
        return `Account ending in ${transaction.bank_account.account_number_last_four}`;
    }
  };
  const successfulTransactions = transaction_details.filter(
    (t) =>
      t.status === TRANSACTION_STATUS.SUCCESSFUL ||
      t.status === TRANSACTION_STATUS.CANCEL_PENDING,
  );
  const voidableTransactions = successfulTransactions.filter(
    (t) => t.status !== TRANSACTION_STATUS.CANCEL_PENDING,
  );
  const successfulTransactionsWithVoidText = voidableTransactions.map((t) => ({
    ...t,
    amount_given: t.amount_given * centsMultiplier,
    amount_processed: t.amount_processed * centsMultiplier,
    voidText: createVoidText(t),
  }));
  const amtPaid = successfulTransactions.reduce(
    (acc, cur) => (acc += cur.amount_processed * centsMultiplier),
    0,
  );
  const totalServiceFees = successfulTransactions.reduce(
    (acc, cur) => (acc += cur.service_fee * centsMultiplier),
    0,
  );
  const totalConvenienceFees = successfulTransactions.reduce(
    (acc, cur) => (acc += cur.convenience_fee * centsMultiplier),
    0,
  );
  const isAtCartRoute = location.pathname === `/pos/cart`;
  return (
    <Box padding=".5rem 0" id="cart-sidebar">
      <BoxWithShadow
        background={constants.colors.WHITE}
        variant="baseStandard"
        padding="0"
        borderRadius="0.25rem"
      >
        <Box padding="1.5rem">
          {isLoading ? (
            <Spinner size="100" />
          ) : (
            <PaymentDetails
              subtotal={false}
              total={total * centsMultiplier}
              lineItems={lineItems}
              fees={[]}
              isMobile={!!lineItems.length}
              collapsibleOnMobile={!!lineItems.length}
              hideTitle={false}
              voidableTransactionDetails={successfulTransactionsWithVoidText}
              hasVoidablePaymentsSection={
                !!successfulTransactionsWithVoidText.length &&
                order.status === ORDER_STATUS.INCOMPLETE
              }
              partialVoidAction={(transaction) =>
                dispatch(
                  initiateVoidTransactionById({
                    id: transaction.id,
                    isDatacapTransaction: isDatacapTransaction(
                      transaction.payment_tender_type,
                      creditCardReaderType,
                    ),
                    payment_attempt_unique_identifier:
                      transaction.payment_attempt_unique_identifier,
                  }),
                )
              }
              voidableAmountPaid={amtPaid}
              remainingBalance={remaining_balance * centsMultiplier}
              serviceFees={totalServiceFees}
              convenienceFees={totalConvenienceFees}
            />
          )}
        </Box>
        <SolidDivider />
        {!isAtCartRoute && Boolean(lineItems.length) && (
          <Box>
            <ButtonWithLink
              extraStyles={`width: 100%;`}
              text="View Cart"
              url={'/pos/cart'}
              isLoading={false}
              variant="secondary"
              dataQa="view-cart"
              textExtraStyles="font-size: 1rem;"
            />
          </Box>
        )}
      </BoxWithShadow>
    </Box>
  );
};

export { CartSidebar };
